import { useState } from "react";
import { toast } from "sonner";
import { Gauge, Plus, Minus, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

export function DriverScoreDialog({ driver }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [score, setScore] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    try { const { data } = await api.get(`/drivers/${driver.id}/score`); setScore(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const patch = async (body) => {
    try { const { data } = await api.patch(`/drivers/${driver.id}/score`, body); setScore(data); setNote(""); toast.success(t("ds.save")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" data-testid="driver-score-btn" title={t("ds.adjust")}><Gauge size={15} /></Button>
      </DialogTrigger>
      <DialogContent data-testid="driver-score-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Gauge size={18} /> {driver.name} — {t("ds.adjust")}</DialogTitle></DialogHeader>
        {!score ? <p className="text-slate-400 py-6">{t("common.loading")}</p> : (
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><div className="text-xs text-slate-400">{t("dd.systemScore")}</div><div className="font-display text-xl font-bold text-slate-800" data-testid="ds-system">{score.system_score}</div></div>
              <div><div className="text-xs text-slate-400">{t("dd.adminAdj")}</div><div className="font-display text-xl font-bold text-slate-800" data-testid="ds-adj">{score.admin_adjustment > 0 ? `+${score.admin_adjustment}` : score.admin_adjustment}</div></div>
              <div><div className="text-xs text-slate-400">{t("dd.finalScore")}</div><div className="font-display text-xl font-bold text-blue-900" data-testid="ds-final">{score.final_score}</div></div>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => patch({ delta: -5 })} data-testid="ds-decrease"><Minus size={14} className="mr-1" /> {t("ds.decrease")} 5</Button>
              <Button variant="outline" size="sm" onClick={() => patch({ delta: 5 })} data-testid="ds-increase"><Plus size={14} className="mr-1" /> {t("ds.increase")} 5</Button>
            </div>
            <div className="space-y-1.5">
              <Label>{t("ds.setRating")}</Label>
              <div className="flex items-center gap-1" data-testid="ds-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => patch({ rating: n })} data-testid={`ds-rating-${n}`}>
                    <Star size={22} className={n <= (score.admin_rating || 0) ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"} />
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("ds.addNote")}</Label>
              <div className="flex gap-2">
                <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="ds-note-input" />
                <Button onClick={() => note.trim() && patch({ note: note.trim() })} disabled={!note.trim()} className="bg-blue-900 hover:bg-blue-800" data-testid="ds-note-save">{t("common.save")}</Button>
              </div>
            </div>
            {(score.notes || []).length > 0 && (
              <div className="border-t border-slate-100 pt-3 space-y-1 max-h-32 overflow-y-auto" data-testid="ds-notes-list">
                {score.notes.slice().reverse().map((n, i) => <p key={i} className="text-xs text-slate-500">“{n.note}” — {n.by}</p>)}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
